import { ROOM_TYPE, ROLE } from './constants.js'

export const ACTION = {
  POST: 'post',
  EDIT_OWN: 'edit_own',
  DELETE_OWN: 'delete_own',
  DELETE_ANY: 'delete_any',
  REACT: 'react',
  PIN: 'pin',
  INVITE: 'invite',
  KICK: 'kick',
  SET_ROLE: 'set_role',
  EDIT_META: 'edit_meta',
  ADD_WRITER: 'add_writer',
  CALL: 'call',
  POLL: 'poll'
}

/** Minimum role required per action, per room type. */
const MATRIX = {
  [ROOM_TYPE.SAVED]: {
    post: ROLE.OWNER,
    edit_own: ROLE.OWNER,
    delete_own: ROLE.OWNER,
    delete_any: ROLE.OWNER,
    react: ROLE.OWNER,
    pin: ROLE.OWNER,
    invite: null,
    kick: null,
    set_role: null,
    edit_meta: ROLE.OWNER,
    add_writer: ROLE.OWNER,
    call: null,
    poll: ROLE.OWNER
  },
  [ROOM_TYPE.DM]: {
    post: ROLE.MEMBER,
    edit_own: ROLE.MEMBER,
    delete_own: ROLE.MEMBER,
    delete_any: null,
    react: ROLE.MEMBER,
    pin: ROLE.MEMBER,
    // Allowed by rank, then closed by headcount below: a direct chat is
    // invited into once, to fetch the other person, and never again.
    invite: ROLE.MEMBER,
    kick: null,
    set_role: null,
    edit_meta: ROLE.MEMBER,
    add_writer: ROLE.MEMBER,
    call: ROLE.MEMBER,
    poll: ROLE.MEMBER
  },
  [ROOM_TYPE.GROUP]: {
    post: ROLE.MEMBER,
    edit_own: ROLE.MEMBER,
    delete_own: ROLE.MEMBER,
    delete_any: ROLE.MODERATOR,
    react: ROLE.MEMBER,
    pin: ROLE.MODERATOR,
    invite: ROLE.MEMBER,
    kick: ROLE.ADMIN,
    set_role: ROLE.ADMIN,
    edit_meta: ROLE.ADMIN,
    add_writer: ROLE.ADMIN,
    call: ROLE.MEMBER,
    poll: ROLE.MEMBER
  },
  [ROOM_TYPE.CHANNEL]: {
    post: ROLE.ADMIN,
    edit_own: ROLE.ADMIN,
    delete_own: ROLE.ADMIN,
    delete_any: ROLE.MODERATOR,
    react: ROLE.MEMBER,
    pin: ROLE.MODERATOR,
    invite: ROLE.MEMBER,
    kick: ROLE.ADMIN,
    set_role: ROLE.OWNER,
    edit_meta: ROLE.ADMIN,
    add_writer: ROLE.ADMIN,
    call: ROLE.ADMIN,
    poll: ROLE.ADMIN
  }
}

/** Room settings that can further restrict what the matrix allows. */
export const DEFAULT_SETTINGS = {
  membersCanInvite: true,
  reactionsEnabled: true,
  readOnly: false,
  slowModeSeconds: 0,
  historyForNewMembers: true,
  disappearingSeconds: 0,
  linkPreviews: true,
  joinsVisible: true
}

export function normalizeSettings (settings) {
  let parsed = settings
  if (typeof settings === 'string') {
    try { parsed = JSON.parse(settings || '{}') } catch { parsed = {} }
  }
  return { ...DEFAULT_SETTINGS, ...(parsed || {}) }
}

/**
 * @param action  one of ACTION
 * @param role    the actor's role in the room
 * @param type    ROOM_TYPE
 * @param settings room settings (raw JSON string or object)
 * @param context  { memberCount } — facts the matrix cannot express
 */
export function can (action, role, type, settings = null, context = null) {
  const table = MATRIX[type]
  if (!table) return false
  const required = table[action]
  if (required === null || required === undefined) return false
  if (role === undefined || role === null) return false
  if (role < required) return false

  const s = normalizeSettings(settings)

  if (s.readOnly && (action === ACTION.POST || action === ACTION.POLL)) {
    return role >= ROLE.ADMIN
  }
  if (!s.reactionsEnabled && action === ACTION.REACT) return false
  if (!s.membersCanInvite && action === ACTION.INVITE) return role >= ROLE.ADMIN

  /*
   * A direct chat is invited into exactly once — to bring in the other
   * person — and then it is shut. That closing is the whole difference
   * between a DM and a group of two, and the matrix cannot state it, because
   * a rank cannot count members.
   *
   * Written as a flat ban it also blocked the founding invite, which is the
   * only way a DM ever acquires its second member: creating one failed with
   * "you do not have permission to invite people to this room", in a room the
   * person had just made.
   *
   * With no count to go on, treat the pair as complete. Refusing an invite
   * that should have been allowed costs a retry; allowing one that should
   * have been refused puts a stranger in someone's private conversation.
   */
  if (action === ACTION.INVITE && type === ROOM_TYPE.DM) {
    const members = context && context.memberCount
    if (typeof members !== 'number' || members >= 2) return false
  }

  return true
}

/**
 * Role changes are only legal if the actor outranks both the target's current
 * role and the role being granted. Owners cannot be demoted by anyone but
 * themselves, which prevents an admin coup.
 */
export function canSetRole (actorRole, targetRole, nextRole, type, settings) {
  if (!can(ACTION.SET_ROLE, actorRole, type, settings)) return false
  if (targetRole >= actorRole && actorRole !== ROLE.OWNER) return false
  if (nextRole >= actorRole && actorRole !== ROLE.OWNER) return false
  if (targetRole === ROLE.OWNER) return false
  return true
}

export function canKick (actorRole, targetRole, type, settings) {
  if (!can(ACTION.KICK, actorRole, type, settings)) return false
  return targetRole < actorRole
}

/** Human-readable summary used by the members screen. */
export function describeRole (role) {
  switch (role) {
    case ROLE.OWNER: return 'Owner'
    case ROLE.ADMIN: return 'Admin'
    case ROLE.MODERATOR: return 'Moderator'
    case ROLE.MEMBER: return 'Member'
    case ROLE.RESTRICTED: return 'Restricted'
    default: return 'Unknown'
  }
}
